/**
 * SPE PRMS volumetric geological oil in place (metric).
 * Volume [million m³] = Area [km2] × h [m] × NtG × φ × (1 − Sw) / Bo
 * Mass [thousand t] = Volume [million m³] × 1000 × ρ [g/cm³]
 */

import {
  getOilAndGasAreaKm2FromProperties,
  OIL_AND_GAS_AREA_PROPERTY_NAME,
} from '@/utils/oilAndGasArea';

export const VOLUMETRIC_POINT_COUNT = 10;

export const VOLUMETRIC_PARAMETERS = [
  { key: 'gross_thickness_m', label: 'Gross thickness', unit: 'm' },
  { key: 'net_to_gross_pct', label: 'Net-to-gross', unit: '%', hint: 'Sandiness coefficient' },
  { key: 'porosity_pct', label: 'Effective porosity', unit: '%', hint: 'Porosity coefficient' },
  { key: 'water_saturation_pct', label: 'Effective water saturation', unit: '%' },
  { key: 'oil_fvf', label: 'Oil formation volume factor (Bo)', unit: 'm³/m³' },
] as const;

export type VolumetricParameterKey = (typeof VOLUMETRIC_PARAMETERS)[number]['key'];

export interface VolumetricPointValues {
  gross_thickness_m?: number | null;
  net_to_gross_pct?: number | null;
  porosity_pct?: number | null;
  water_saturation_pct?: number | null;
  oil_fvf?: number | null;
}

export interface VolumetricPointColumn extends VolumetricPointValues {
  column_index: number;
  geological_thousand_tons?: number | null;
}

export interface VolumetricEstimateDraft {
  area_km2?: number | null;
  oil_density_g_cm3?: number | null;
  points: VolumetricPointColumn[];
  total_geological_thousand_tons?: number | null;
}

export interface VolumetricCalculationResult {
  area_km2: number;
  oil_density_g_cm3: number;
  active_point_count: number;
  area_per_point_km2: number;
  total_geological_thousand_tons: number;
  points: VolumetricPointColumn[];
}

const PROPERTY_PREFILL_MAP: Record<string, VolumetricParameterKey | '_oil_saturation_pct'> = {
  'Sandiness coefficient': 'net_to_gross_pct',
  'Porosity coefficient': 'porosity_pct',
  'Average effective water saturation': 'water_saturation_pct',
  'Average effective oil saturation': '_oil_saturation_pct',
  'Oil formation volume factor': 'oil_fvf',
};

export const AREA_PROPERTY_NAME = OIL_AND_GAS_AREA_PROPERTY_NAME;
export const OIL_DENSITY_PROPERTY_NAME = 'Oil density at surface conditions';

function isBlank(v: unknown): boolean {
  return v === null || v === undefined || v === '';
}

function pointValues(point: VolumetricPointValues): Record<VolumetricParameterKey, unknown> {
  return {
    gross_thickness_m: point.gross_thickness_m,
    net_to_gross_pct: point.net_to_gross_pct,
    porosity_pct: point.porosity_pct,
    water_saturation_pct: point.water_saturation_pct,
    oil_fvf: point.oil_fvf,
  };
}

export function emptyVolumetricPoints(): VolumetricPointColumn[] {
  return Array.from({ length: VOLUMETRIC_POINT_COUNT }, (_, i) => ({ column_index: i }));
}

export function emptyVolumetricDraft(): VolumetricEstimateDraft {
  return {
    area_km2: null,
    oil_density_g_cm3: null,
    points: emptyVolumetricPoints(),
    total_geological_thousand_tons: null,
  };
}

export function columnIsEmpty(point: VolumetricPointValues): boolean {
  return Object.values(pointValues(point)).every(isBlank);
}

export function columnIsComplete(point: VolumetricPointValues): boolean {
  return Object.values(pointValues(point)).every((v) => !isBlank(v));
}

export function buildPropertyValueMap(
  properties: Array<{ attribute_name: string; float_value?: number; integer_value?: number }>,
): Record<string, number | null> {
  const map: Record<string, number | null> = {};
  for (const p of properties) {
    const val = p.float_value ?? p.integer_value ?? null;
    map[p.attribute_name] = val;
  }
  return map;
}

export function prefillColumnFromProperties(
  propertiesByName: Record<string, number | null>,
): VolumetricPointValues {
  const result: VolumetricPointValues = {};
  let oilSat: number | null = null;

  for (const [propName, target] of Object.entries(PROPERTY_PREFILL_MAP)) {
    const val = propertiesByName[propName];
    if (target === '_oil_saturation_pct') {
      oilSat = val;
    } else if (val != null) {
      result[target] = val;
    }
  }

  if (result.water_saturation_pct == null && oilSat != null) {
    result.water_saturation_pct = Math.max(0, Math.min(100, 100 - oilSat));
  }

  return result;
}

export function validateVolumetricInput(
  areaKm2: number | null | undefined,
  oilDensityGcm3: number | null | undefined,
  points: VolumetricPointValues[],
): string[] {
  const errors: string[] = [];
  if (areaKm2 == null || !(Number(areaKm2) > 0)) {
    errors.push('Enter a positive mapped area (km2) for the layer.');
  }
  if (oilDensityGcm3 == null || !(Number(oilDensityGcm3) > 0)) {
    errors.push('Enter oil density at surface conditions (g/cm³) for the layer.');
  }

  const active: number[] = [];
  points.forEach((point, idx) => {
    const empty = columnIsEmpty(point);
    const complete = columnIsComplete(point);
    if (!empty && !complete) {
      errors.push(`Point ${idx + 1}: fill every parameter or leave the entire column empty.`);
    }
    if (complete) active.push(idx);
  });

  if (!active.length) {
    errors.push('Enter at least one complete measurement point (column).');
  }

  for (const idx of active) {
    const p = points[idx];
    const vals = pointValues(p);
    if (!(Number(vals.gross_thickness_m) > 0)) {
      errors.push(`Point ${idx + 1}: gross thickness must be positive.`);
    }
    for (const [key, label] of [
      ['net_to_gross_pct', 'net-to-gross'],
      ['porosity_pct', 'effective porosity'],
      ['water_saturation_pct', 'effective water saturation'],
    ] as const) {
      const v = Number(vals[key]);
      if (!(v > 0) || v > 100) {
        errors.push(`Point ${idx + 1}: ${label} must be in (0, 100]%.`);
      }
    }
    if (!(Number(vals.oil_fvf) > 0)) {
      errors.push(`Point ${idx + 1}: oil formation volume factor (Bo) must be positive.`);
    }
  }

  return errors;
}

function stockTankVolumeMillionSm3(areaKm2: number, point: VolumetricPointValues): number {
  const h = Number(point.gross_thickness_m);
  const ntg = Number(point.net_to_gross_pct) / 100;
  const phi = Number(point.porosity_pct) / 100;
  const sw = Number(point.water_saturation_pct) / 100;
  const bo = Number(point.oil_fvf);
  return areaKm2 * h * ntg * phi * (1 - sw) / bo;
}

function volumeToThousandTons(volumeMillionSm3: number, oilDensityGcm3: number): number {
  return volumeMillionSm3 * 1000 * oilDensityGcm3;
}

export function calculateGeologicalThousandTonsPoint(
  areaKm2: number,
  point: VolumetricPointValues,
  oilDensityGcm3: number,
): number {
  const volume = stockTankVolumeMillionSm3(areaKm2, point);
  return volumeToThousandTons(volume, oilDensityGcm3);
}

export function calculateVolumetricGeological(
  areaKm2: number,
  oilDensityGcm3: number,
  points: VolumetricPointColumn[],
): VolumetricCalculationResult {
  const errors = validateVolumetricInput(areaKm2, oilDensityGcm3, points);
  if (errors.length) throw new Error(errors.join(' '));

  const active = points
    .map((p, idx) => ({ idx, p }))
    .filter(({ p }) => columnIsComplete(p));
  const n = active.length;
  const areaPerPoint = areaKm2 / n;
  const density = Number(oilDensityGcm3);

  const pointResults: VolumetricPointColumn[] = [];
  let total = 0;

  for (const { idx, p } of active) {
    const kt = calculateGeologicalThousandTonsPoint(areaPerPoint, p, density);
    total += kt;
    pointResults.push({ ...p, column_index: idx, geological_thousand_tons: kt });
  }

  return {
    area_km2: areaKm2,
    oil_density_g_cm3: density,
    active_point_count: n,
    area_per_point_km2: areaPerPoint,
    total_geological_thousand_tons: total,
    points: pointResults,
  };
}

export function mergePrefillIntoDraft(
  draft: VolumetricEstimateDraft,
  propertiesByName: Record<string, number | null>,
  properties?: Array<{
    attribute_name: string;
    float_value?: number | null;
    integer_value?: number | null;
    attribute_unit?: string | null;
  }>,
): VolumetricEstimateDraft {
  const area = properties
    ? getOilAndGasAreaKm2FromProperties(properties)
    : propertiesByName[AREA_PROPERTY_NAME];
  const density = propertiesByName[OIL_DENSITY_PROPERTY_NAME];
  const col0 = { ...draft.points[0], ...prefillColumnFromProperties(propertiesByName) };
  const points = [...draft.points];
  points[0] = { ...col0, column_index: 0 };
  return {
    ...draft,
    area_km2: draft.area_km2 ?? area ?? null,
    oil_density_g_cm3: draft.oil_density_g_cm3 ?? density ?? null,
    points,
  };
}

function pointParameterValues(point: VolumetricPointColumn): VolumetricPointValues {
  return {
    gross_thickness_m: point.gross_thickness_m ?? null,
    net_to_gross_pct: point.net_to_gross_pct ?? null,
    porosity_pct: point.porosity_pct ?? null,
    water_saturation_pct: point.water_saturation_pct ?? null,
    oil_fvf: point.oil_fvf ?? null,
  };
}

/** Copy all parameter values from one measurement column to another. */
export function copyVolumetricColumn(
  draft: VolumetricEstimateDraft,
  fromCol: number,
  toCol: number,
): VolumetricEstimateDraft {
  if (fromCol === toCol) return draft;
  const source = draft.points[fromCol];
  if (!source) return draft;

  const values = pointParameterValues(source);
  const points = draft.points.map((p, idx) =>
    idx === toCol
      ? {
          column_index: toCol,
          ...values,
          geological_thousand_tons: null,
        }
      : p,
  );

  return {
    ...draft,
    points,
    total_geological_thousand_tons: null,
  };
}

/** Clear every parameter cell in a measurement column. */
export function clearVolumetricColumn(
  draft: VolumetricEstimateDraft,
  col: number,
): VolumetricEstimateDraft {
  const points = draft.points.map((p, idx) =>
    idx === col
      ? { column_index: col, geological_thousand_tons: null }
      : p,
  );

  return {
    ...draft,
    points,
    total_geological_thousand_tons: null,
  };
}
